import { useEffect, useMemo, useState } from 'react';
import PhoneInput from 'react-phone-input-2';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Radio,
  RadioGroup
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { debounce } from 'lodash';
import { encryptPassword } from '../../utils/encryptPassword';
import { basicErrorHandler, useURLParams, validatePassword } from '../../utils/helpers';
import { fetchPublicJob } from '../../actions/job';
import { Input } from '../../Components/common/Form/Input/Input';
import styles from './QuickApplyEmbedded.module.scss';
import { labelStyle, textFieldStyles } from './styles';
import CheckBox from '../../Components/Form/Preference/CheckBox/CheckBox';
import MainButton from '../../Components/common/buttons/MainButton';
import { buttonVariants } from '../../Components/common/buttons/types';
import { validateUser } from '../../actions/user';
import { applyJobEmbedded } from '../../actions/jobs';
import configData from '../../config.json';
import api from '../../api/apiSettings';
import { setNotification } from '../../actions/appNotifications';
import {
  appNotificationsTypes,
  CV_FILE_ALLOWED_FORMATS,
  MAX_CV_FILE_SIZE,
  validationMessages
} from '../../utils/constants';
import colors from '../../scss/colors';
import { emailRegExp } from '../../utils/regexps';
import ValidationErrorLabel from '../../Components/common/ValidationErrorLabel/ValidationErrorLabel';
import SessionNotice from '../../Components/quickapplyv2/sessionNotice/sessionNotice';
import { getGuestDetails, saveGuestDetails } from '../../utils/quickApplySession';

const dialCodeDigits = (phoneCode) => `${phoneCode || ''}`.replace('+', '');

// The session store keeps the national number separately from the dial code (as
// Quick Apply does), whereas this form's PhoneInput value includes the dial code.
const toSessionDetails = (fields) => ({
  firstName: fields.firstName,
  lastName: fields.lastName,
  email: fields.email,
  Phone: `${fields.phone || ''}`.replace(new RegExp(`^${dialCodeDigits(fields.phoneCode)}`), ''),
  PhoneCountryCode: fields.phoneCode,
  countryName: fields.countryName
});

const fromSessionDetails = (details) => ({
  firstName: details?.firstName || '',
  lastName: details?.lastName || '',
  email: details?.email || '',
  phone: details?.Phone ? `${dialCodeDigits(details.PhoneCountryCode)}${details.Phone}` : '',
  phoneCode: details?.Phone ? details.PhoneCountryCode : '',
  countryName: details?.Phone ? details.countryName : ''
});

const QuickApplyEmbedded = () => {
  const [t] = useTranslation();
  const params = useURLParams();
  const { id } = useParams();
  const job = useSelector((state) => state.job.response);
  const IsExistUser = useSelector((state) => state.user?.profile?.validation?.Data?.IsExistUser);
  const validateUserMessage = useSelector((state) => state.user?.profile?.validation?.Data?.Message);
  const dispatch = useDispatch();
  const [applicationSuccessful, setApplicationSuccessful] = useState(false);
  const [applicationPending, setApplicationPending] = useState(false);
  const [, setRegistrationSuccessful] = useState(false);

  // Guest-only convenience: basic contact details kept for this browser session.
  // Resolved once, before the form state below is seeded from it.
  const [restoredDetails] = useState(getGuestDetails);

  const [fields, setFields] = useState(() => ({
    ...fromSessionDetails(restoredDetails),
    password: '',
    cv: null,
    cvName: '',
    Question1: '',
    Question2: '',
    Question3: '',
    Answer1: null,
    Answer2: null,
    Answer3: null,
    IsManageConcent: false,
    Source: '',
    PageUrl: ''
  }));

  const initialErrorValidationValue = {
    phone: false,
    firstName: false,
    lastName: false,
    email: false,
    cv: false
  };

  const [errors, setErrors] = useState(initialErrorValidationValue);
  const [touched, setTouched] = useState(initialErrorValidationValue);
  const validationMessage = useMemo(() => validationMessages(t), [t]);
  const [showPassword, setShowPassword] = useState(false);
  const [isPasswordValid, setIsPasswordValid] = useState({
    charactersqty: false,
    lowercase: false,
    uppercase: false,
    numberorsymbol: false
  });

  useEffect(() => {
    if (id) dispatch(fetchPublicJob(id));
  }, [id]);

  const validate = () => {
    const validationObj = {
      ...initialErrorValidationValue
    };
    const newErrors = { ...errors };

    if (!fields.phone) {
      validationObj.phone = true;
      newErrors.phone = validationMessage.PHONE_NUMBER_REQ;
    }
    if (!fields.phone && (!fields.phoneCode === 'undefined' || !fields.phone?.length < 4)) {
      validationObj.phone = true;
      newErrors.phone = validationMessage.PHONE_NUMBER_INVALID;
    }
    if (!fields.firstName) {
      validationObj.firstName = true;
      newErrors.firstName = 'First name is required';
    }
    if (!fields.lastName) {
      validationObj.lastName = true;
      newErrors.lastName = 'Last name is required';
    }
    if (!fields.email || (!!fields.email?.length && !fields.email.match(emailRegExp)?.length)) {
      validationObj.email = true;
      newErrors.email = 'Email is not valid';
    }
    if (!fields.cv) {
      validationObj.cv = true;
      newErrors.cv = validationMessage.CV_REQUIRED;
    }

    setErrors(newErrors);

    return validationObj;
  };

  const changePasswordHandler = (e) => {
    setIsPasswordValid(validatePassword(e.target.value));
    setFields({ ...fields, password: e.target.value });
  };

  const handleRedirectToApply = (jobId) => {
    window.open(`${configData.REACT_APP_PUBLIC_URL}/interaction?jobId=${jobId}`, '_blank');
  };

  const registerHandler = async () => {
    const regData = {
      FirstName: fields.firstName,
      LastName: fields.lastName,
      Phone: fields.phone,
      PhoneCountryCode: fields.phoneCode,
      PrimaryEmail: fields.email,
      Password: encryptPassword(fields.password)
    };

    try {
      await api.post('v1/user/quick-registration', regData);
      setRegistrationSuccessful(true);
    } catch (e) {
      setRegistrationSuccessful(false);
      dispatch(
        setNotification({
          type: appNotificationsTypes.ERROR,
          message: e?.response?.data?.title || e.message,
          duration: 3000
        })
      );
    }
  };

  const handleApplyJob = () => {
    const validationObj = validate();
    const validationWrongValues = Object.values(validationObj).filter(Boolean);

    if (!job && !job?.Id) {
      return dispatch(
        setNotification({
          type: appNotificationsTypes.ERROR,
          message: 'The job is no longer valid. Please try with an active job.',
          duration: 3000
        })
      );
    }

    setApplicationPending(true);

    if (!validationWrongValues?.length) {
      dispatch(
        applyJobEmbedded({
          Email: fields.email,
          FirstName: fields.firstName,
          LastName: fields.lastName,
          PhoneCountryCode: fields.phoneCode,
          Country: fields.countryName,
          IsManageConcent: fields.IsManageConcent,
          JobId: job.Id,
          Phone: fields.phone,
          Answer1: fields.Answer1,
          Answer2: fields.Answer2,
          Answer3: fields.Answer3,
          Cv: fields?.cv,
          Source: fields.Source,
          PageUrl: fields.PageUrl,
          PageTitle: job?.JobTitle || '',
          ApplicationSource: params?.utm_source || '',
          ApplicationSourceDrillDown1: params?.utm_medium || '',
          ApplicationSourceDrillDown2: params?.utm_campaign || '',
          // eslint-disable-next-line no-underscore-dangle -- _hsenc is a HubSpot URL query param name
          HubSpotCampaign: params?._hsenc || ''
        })
      )
        .then(async () => {
          saveGuestDetails(toSessionDetails(fields));
          if (fields.password?.length > 0) {
            await registerHandler();
            await setApplicationSuccessful(true);
            await setApplicationPending(false);
            return true;
          }
          setApplicationSuccessful(true);
          setApplicationPending(false);
          return undefined;
        })
        .catch((error) => {
          setApplicationPending(false);
          basicErrorHandler(dispatch, error.response.data.title || error.response.data);
          console.error('error: ', error);
        });
    } else {
      setApplicationPending(false);
      dispatch(
        setNotification({
          type: appNotificationsTypes.ERROR,
          message: 'Please ensure all required fields are filled out correctly.',
          duration: 3000
        })
      );
    }
    return undefined;
  };

  const validateInputFile = (file) => {
    // setCvReq(false);
    const currentErrors = [];
    if (file && !CV_FILE_ALLOWED_FORMATS.includes(file.name.toLowerCase().split('.').pop()))
      currentErrors.push(validationMessage.CV_ALLOWED_FORMATS);
    if (file.size > MAX_CV_FILE_SIZE) currentErrors.push(validationMessage.CV_ALLOWED_FILE_SIZE);
    return currentErrors;
  };

  const onChangeFile = (e) => {
    setErrors({ ...errors, cv: '' });
    const validationErrors = validateInputFile(e.target.files[0]);

    if (validationErrors?.length) {
      setErrors({ ...errors, cv: validationErrors });
      return;
    }

    if (e.target.files) {
      setFields({
        ...fields,
        cv: e.target.files[0],
        cvName: e.target.files[0].name
      });
    }
  };

  const debouncedValidateEmail = debounce((email, jobId) => {
    dispatch(validateUser(jobId, email));
  }, 300);

  const emailValidationClass = () => {
    if (errors.email) {
      return styles.inputErrorWrapper;
    }

    if (IsExistUser) {
      return styles.inputWrapperWithErrorMessage;
    }
    return styles.inputWrapper;
  };

  useEffect(() => {
    // A restored email is present before the job loads, so wait for the job id.
    if (fields.email && job?.Id) {
      debouncedValidateEmail(fields.email, job.Id);
    }

    return () => {
      debouncedValidateEmail.cancel();
    };
  }, [fields.email, job?.Id, dispatch]);

  useEffect(() => {
    const sendHeight = () => {
      const height = document.documentElement.offsetHeight;
      window.parent.postMessage({ type: 'asdIframeHeight', height }, '*');
    };

    if (applicationSuccessful) {
      window.parent.postMessage({ type: 'applicationSuccessful' }, '*');
    }

    sendHeight();
    window.addEventListener('resize', sendHeight);

    return () => {
      window.removeEventListener('resize', sendHeight);
    };
  }, [document, fields.email?.length, job, errors, applicationSuccessful]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data.type === 'PARENT_URL') {
        const pageUrl = typeof event.data === 'string' ? event.data : event.data?.url ?? '';
        setFields((prevFields) => ({
          ...prevFields,
          PageUrl: pageUrl
        }));
      }
      if (event.data.type === 'ORIGIN') {
        setFields((prevFields) => ({
          ...prevFields,
          Source: event.data?.url
        }));
      }
    };
    window.addEventListener('message', handleMessage);
    window.parent.postMessage({ type: 'GET_PARENT_URL' }, '*');
    window.parent.postMessage({ type: 'GET_ORIGIN' }, '*');
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  const isJobApplied = validateUserMessage?.length > 0 && validateUserMessage.includes('Thanks');

  const renderSubmitContent = () => {
    if (isJobApplied) return 'APPLICATION RECEIVED';
    if (applicationPending) return <CircularProgress size="1rem" sx={{ color: colors.WHITE }} />;
    return 'SUBMIT YOUR APPLICATION';
  };

  return (
    <section className={styles.quickApplyContainer}>
      <div className={styles.quickApplyWrapper}>
        <h3>Apply Now</h3>
        <div className={styles.quickApplyForm}>
          {restoredDetails && (
            <div className="mb-4">
              <SessionNotice variant="restored" message={t('jobs.quickapply.sessionrestorednotice')} />
            </div>
          )}
          <div className="mb-4">
            <Input
              classes={errors.firstName ? styles.inputErrorWrapper : styles.inputWrapper}
              onChange={(e) => {
                setFields({ ...fields, firstName: e.target.value });
                setErrors({ ...errors, firstName: '' });
              }}
              onBlur={() => setTouched({ ...touched, firstName: true })}
              value={fields.firstName}
              isClosedVariant
              type="text"
              label="First Name*"
            />
            {errors.firstName && <ValidationErrorLabel fontSize="14px" message={errors.firstName} />}
          </div>
          <div className="mb-4">
            <Input
              classes={errors.lastName ? styles.inputErrorWrapper : styles.inputWrapper}
              onChange={(e) => {
                setFields({ ...fields, lastName: e.target.value });
                setErrors({ ...errors, lastName: '' });
              }}
              onBlur={() => setTouched({ ...touched, lastName: true })}
              value={fields.lastName}
              isClosedVariant
              type="text"
              label="Last Name*"
            />
            {errors.lastName && <ValidationErrorLabel fontSize="14px" message={errors.lastName} />}
          </div>
          <div className="mb-4">
            <Input
              classes={emailValidationClass()}
              onChange={(e) => {
                setFields({ ...fields, email: e.target.value });
                setErrors({ ...errors, email: '' });
              }}
              onBlur={() => setTouched({ ...touched, email: true })}
              value={fields.email}
              isClosedVariant
              type="text"
              label="Email*"
            />
            {errors.email && <ValidationErrorLabel fontSize="14px" message={errors.email} />}
            {isJobApplied && (
              <p className={styles.jobApplied}>
                <CheckCircleIcon className="m-2 my-0" />
                {validateUserMessage}
              </p>
            )}
            {IsExistUser && fields.email?.length > 0 && (
              <p className={styles.accountExist}>
                Looks like you have an account with us. Use your saved information and
                <span
                  role="presentation"
                  onClick={() => handleRedirectToApply(job.Id)}
                  className={styles.applyWithAccount}
                >
                  {' '}
                  apply with your account
                </span>{' '}
                here
              </p>
            )}
          </div>
          <div className={styles.phoneInputWrapper}>
            <label className={styles.customLabel} htmlFor="phoneNumber">
              Phone*
            </label>
            <PhoneInput
              inputStyle={{
                width: '100%',
                marginBottom: '20px',
                paddingLeft: '60px',
                height: '40px',
                background: colors.WHITE,
                borderColor: errors.phone ? colors.ERROR_RED : colors.LIGHT_BLUE,
                borderRadius: '20px',
                fontSize: '16px'
              }}
              buttonStyle={{
                borderRadius: '20px 0px 0px 20px',
                background: colors.WHITE,
                border: 'none',
                marginLeft: '15px',
                height: '38px',
                marginTop: '1px'
              }}
              country="gb"
              value={fields.phone}
              onBlur={() => setTouched({ ...touched, phone: true })}
              onChange={(newPhone, code) => {
                setFields({
                  ...fields,
                  phone: newPhone,
                  phoneCode: `${code.dialCode ? '+' : ''}${code.dialCode}`,
                  countryName: `${code.name}`
                });
                setErrors({ ...errors, phone: '' });
              }}
              id="phoneNumber"
            />
            {errors.phone && <ValidationErrorLabel fontSize="14px" message={errors.phone} />}
          </div>
          {!restoredDetails && (
            <div className="mb-4">
              <SessionNotice message={t('jobs.quickapply.sessionstoragenotice')} />
            </div>
          )}
          <div className="mb-4">
            <Input
              classes={errors.cv ? styles.inputErrorWrapper : styles.inputWrapper}
              onChange={(e) => {
                onChangeFile(e);
                setErrors({ ...errors, cv: '' });
              }}
              onBlur={() => setTouched({ ...touched, email: true })}
              accept=".pdf,.docx,.doc"
              isClosedVariant
              type="file"
              label="CV Upload*"
              description={
                <>
                  Scanned files or files with embedded images will not be accepted.
                  <br />
                  Allowed file extensions are .rtf, .doc, .docx, or .pdf
                </>
              }
            />
            {errors.cv && <ValidationErrorLabel fontSize="14px" message={errors.cv} />}
          </div>
        </div>
        {job && (job.Question1 || job.Question2 || job.Question3) && (
          <div className={styles.quickApplyPreScreening}>
            <h3>Job specific requirements</h3>
            <h5>
              Help us understand if you are able to meet some of the essential requirements of this role. Please ensure
              these questions are correct before submitting
            </h5>
            <div className={styles.questions}>
              {job && job.Question1 && (
                <FormControl className="mb-3">
                  <FormLabel id="demo-controlled-radio-buttons-group" className="pb-2">
                    {job.Question1}
                  </FormLabel>
                  <RadioGroup
                    aria-labelledby="demo-controlled-radio-buttons-group"
                    name="controlled-radio-buttons-group"
                    value={fields.Answer1}
                    onChange={(event) => setFields({ ...fields, Answer1: event.target.value || null })}
                  >
                    <FormControlLabel value control={<Radio className="py-0" />} label="Yes" />
                    <FormControlLabel value={false} control={<Radio className="py-0" />} label="No" />
                  </RadioGroup>
                </FormControl>
              )}

              {job && job.Question2 && (
                <FormControl className="mb-3">
                  <FormLabel id="demo-controlled-radio-buttons-group" className="pb-2">
                    {job.Question2}
                  </FormLabel>
                  <RadioGroup
                    aria-labelledby="demo-controlled-radio-buttons-group"
                    name="controlled-radio-buttons-group"
                    value={fields.Answer2}
                    onChange={(event) => setFields({ ...fields, Answer2: event.target.value || null })}
                  >
                    <FormControlLabel value control={<Radio className="py-0" />} label="Yes" />
                    <FormControlLabel value={false} control={<Radio className="py-0" />} label="No" />
                  </RadioGroup>
                </FormControl>
              )}

              {job && job.Question3 && (
                <FormControl className="mb-3">
                  <FormLabel id="demo-controlled-radio-buttons-group" className="pb-2">
                    {job.Question3}
                  </FormLabel>
                  <RadioGroup
                    aria-labelledby="demo-controlled-radio-buttons-group"
                    name="controlled-radio-buttons-group"
                    value={fields.Answer3}
                    onChange={(event) => setFields({ ...fields, Answer3: event.target.value || null })}
                  >
                    <FormControlLabel value control={<Radio className="py-0" />} label="Yes" />
                    <FormControlLabel value={false} control={<Radio className="py-0" />} label="No" />
                  </RadioGroup>
                </FormControl>
              )}
            </div>
          </div>
        )}
        {!IsExistUser && fields.email?.length > 0 && (
          <div className={styles.createAccount}>
            <h4>Create an account by simply setting a password</h4>
            <div className={styles.createAccountFields}>
              <OutlinedInput
                fullWidth
                value={fields.password}
                onChange={changePasswordHandler}
                type={showPassword ? 'text' : 'password'}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((show) => !show)}
                      onMouseDown={(e) => e.preventDefault()}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                sx={textFieldStyles}
              />
            </div>
            <div className={styles.checkboxContainer}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isPasswordValid.charactersqty}
                    name="At least 8 characters"
                    icon={<RadioButtonUncheckedIcon fontSize="14px" />}
                    checkedIcon={<RadioButtonCheckedIcon fontSize="14px" />}
                  />
                }
                label={t('common.passwordvalidation.charactersqty')}
                sx={labelStyle}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isPasswordValid.lowercase}
                    name="One lowercase character"
                    icon={<RadioButtonUncheckedIcon fontSize="14px" />}
                    checkedIcon={<RadioButtonCheckedIcon fontSize="14px" />}
                  />
                }
                label={t('common.passwordvalidation.lowercase')}
                sx={labelStyle}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isPasswordValid.uppercase}
                    name="One uppercase character"
                    icon={<RadioButtonUncheckedIcon fontSize="14px" />}
                    checkedIcon={<RadioButtonCheckedIcon fontSize="14px" />}
                  />
                }
                label={t('common.passwordvalidation.uppercase')}
                sx={labelStyle}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isPasswordValid.numberorsymbol}
                    name="One number or symbol"
                    icon={<RadioButtonUncheckedIcon fontSize="14px" />}
                    checkedIcon={<RadioButtonCheckedIcon fontSize="14px" />}
                  />
                }
                label={t('common.passwordvalidation.numberorsymbol')}
                sx={labelStyle}
              />
            </div>
          </div>
        )}
        <div className={styles.termsAndConditions}>
          <p className="mb-3">
            By clicking ‘Apply” you acknowledge that you have reviewed and agreed to our{' '}
            <a href="https://www.airswift.com/terms-conditions" rel="noreferrer" target="_blank">
              Terms and Conditions
            </a>{' '}
            and{' '}
            <a href="https://www.airswift.com/privacy-cookie-policy/" rel="noreferrer" target="_blank">
              Privacy Policy
            </a>
          </p>
        </div>
        <div className={styles.consentText}>
          <CheckBox
            selected={fields.IsManageConcent}
            className={styles.consentText}
            label="Please tick here if you would like to signup to Airswift’s STEM Careers newsletter"
            onChange={(e) => setFields({ ...fields, IsManageConcent: e.target.checked })}
          />
        </div>
        <div className="d-flex flex-row text-center justify-content-center">
          <MainButton
            variant={isJobApplied ? buttonVariants.DISABLED : buttonVariants.LIGHT_BLUE}
            rounded
            size="xmedium"
            action={() => handleApplyJob()}
            disabled={isJobApplied === true || applicationPending}
            sx={{ width: '100%', marginTop: '1rem', fontSize: '14px', height: '40px' }}
          >
            {renderSubmitContent()}
          </MainButton>
        </div>
      </div>
    </section>
  );
};

export default QuickApplyEmbedded;
